import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../Modal';
import { LeadSourceDto, CreateLeadSourceDto, UpdateLeadSourceDto } from '../../services/settingsService';

const ModalContent = styled.div`
  padding: 24px;
  width: 100%;
  max-width: 500px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  
  &.required::after {
    content: " *";
    color: var(--error-color);
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  
  input[type="checkbox"] {
    margin: 0;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
`;

const StatsSection = styled.div`
  background: var(--background-light);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 16px;
  margin-bottom: 16px;
`;

const StatsTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    
    &:hover {
      background: var(--primary-light); color: var(--primary-color);
    }
    
    &:disabled {
      background: var(--border-color);
      cursor: not-allowed;
    }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--hover-background);
    }
  `}
`;

interface LeadSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (leadSource: CreateLeadSourceDto | UpdateLeadSourceDto) => Promise<void>;
  leadSource?: LeadSourceDto | null;
  title: string;
}

const LeadSourceModal: React.FC<LeadSourceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  leadSource,
  title
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (leadSource) {
        setFormData({
          name: leadSource.name,
          description: leadSource.description || '',
          isActive: leadSource.isActive
        });
      } else {
        setFormData({
          name: '',
          description: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [isOpen, leadSource]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'שם המקור הוא שדה חובה';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const leadSourceData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        isActive: formData.isActive
      };

      await onSave(leadSourceData);
      onClose();
    } catch (error) {
      console.error('שגיאה בשמירת מקור ליד:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <FormSection>
          <FormGroup>
            <Label className="required">שם המקור</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="הזן שם מקור ליד"
              hasError={!!errors.name}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>תיאור</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="הזן תיאור (אופציונלי)"
            />
          </FormGroup>

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
              פעיל
            </CheckboxLabel>
          </CheckboxGroup>

          {leadSource && (
            <StatsSection>
              <StatsTitle>סטטיסטיקות</StatsTitle>
              <StatsGrid>
                <StatItem>
                  <StatValue>{leadSource.leadsCount}</StatValue>
                  <StatLabel>ליידים החודש</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue>{leadSource.successRate.toFixed(1)}%</StatValue>
                  <StatLabel>אחוז הצלחה</StatLabel>
                </StatItem>
              </StatsGrid>
            </StatsSection>
          )}
        </FormSection>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'שומר...' : 'שמירה'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LeadSourceModal;